import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Lock, CheckCircle2, AlertCircle, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { fetchAPI } from "../lib/utils";

export default function ResetPassword() {
	const navigate = useNavigate();
	const { t } = useLanguage();
	const [searchParams] = useSearchParams();
	const token = (searchParams.get("token") || "").trim();

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [done, setDone] = useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		if (password !== confirmPassword) {
			setError(t("passwordsDoNotMatch"));
			return;
		}
		if (password.length < 6) {
			setError(t("passwordTooShort"));
			return;
		}
		setIsLoading(true);
		try {
			await fetchAPI("/auth/reset-password", {
				method: "POST",
				body: JSON.stringify({ token, password }),
			});
			setDone(true);
			toast.success(t("resetPasswordSuccess"));
		} catch (err) {
			setError(err?.message || t("resetPasswordError"));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
			<Card className="w-full max-w-md bg-card border-border rounded-sm">
				<CardHeader className="text-center">
					<CardTitle className="font-heading uppercase tracking-wider text-2xl">
						{t("resetPasswordTitle")}
					</CardTitle>
					<CardDescription>{t("resetPasswordSubtitle")}</CardDescription>
				</CardHeader>
				<CardContent>
					{!token ? (
						<div className="space-y-4 text-center">
							<div className="flex items-center gap-2 justify-center text-blaze text-sm">
								<AlertCircle className="w-4 h-4" />
								{t("resetPasswordInvalidLink")}
							</div>
							<Button
								onClick={() => navigate("/login")}
								className="w-full h-11 bg-volt hover:bg-volt/90 text-white font-bold uppercase tracking-wider rounded-sm"
							>
								{t("backToLogin")}
							</Button>
						</div>
					) : done ? (
						<div className="space-y-4 text-center">
							<div className="flex items-center gap-2 justify-center text-acid text-sm">
								<CheckCircle2 className="w-4 h-4" />
								{t("resetPasswordSuccess")}
							</div>
							<Button
								onClick={() => navigate("/login")}
								className="w-full h-11 bg-volt hover:bg-volt/90 text-white font-bold uppercase tracking-wider rounded-sm"
							>
								{t("goToLogin")}
								<ChevronRight className="w-4 h-4 ml-2" />
							</Button>
						</div>
					) : (
						<form onSubmit={handleSubmit} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="reset-password" className="label-tactical">
									{t("newPassword")}
								</Label>
								<div className="relative">
									<Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id="reset-password"
										type="password"
										placeholder="••••••••"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										className="pl-10 bg-secondary border-transparent h-12 rounded-sm"
										required
										data-testid="reset-password-input"
									/>
								</div>
							</div>
							<div className="space-y-2">
								<Label htmlFor="reset-confirm" className="label-tactical">
									{t("confirmPassword")}
								</Label>
								<div className="relative">
									<Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id="reset-confirm"
										type="password"
										placeholder="••••••••"
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										className="pl-10 bg-secondary border-transparent h-12 rounded-sm"
										required
										data-testid="reset-confirm-input"
									/>
								</div>
							</div>
							{error && (
								<div className="p-3 bg-blaze/10 border border-blaze/20 rounded-sm text-blaze text-sm">
									{error}
								</div>
							)}
							<Button
								type="submit"
								className="w-full h-12 bg-volt hover:bg-volt/90 text-white font-bold uppercase tracking-wider rounded-sm"
								disabled={isLoading}
								data-testid="reset-submit-btn"
							>
								{isLoading ? t("saving") : t("resetPasswordCta")}
							</Button>
						</form>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
